import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from "@models/companies/company";
import { ExternalSourceType } from "@models/integrations/external-source-type.enum";
import { MaskedInputComponent } from "@shared/controls/masked-input/masked-input.component";
import { AuxiliaryApiService } from "@shared/services/auxiliary-api.service";
import { acnValidator } from "@shared/validators/acn.validator";
import { companyNameValidator } from "@shared/validators/compny-name.validator";
import {
  IntegrationsEntitySearchControlComponent
} from "../../controls/integrations-entity-search-control/integrations-entity-search-control.component";
import { ReusableFormGroupComponent } from "../reusable-form-group/reusable-form-group.component";

export interface CompanyNameAcnFormGroupControls {
  name: FormControl<string | null>;
  acn: FormControl<string | null>;
}

export type CompanyNameAcnFormGroup = FormGroup<CompanyNameAcnFormGroupControls>

@Component({
  selector: 'app-company-name-acn-form-group',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MaskedInputComponent,
    IntegrationsEntitySearchControlComponent,
  ],
  templateUrl: 'company-name-acn-form-group.component.html',
  styleUrl: 'company-name-acn-form-group.component.scss'
})
export class CompanyNameAcnFormGroupComponent extends ReusableFormGroupComponent<CompanyNameAcnFormGroup> {
  private abnLookupService = inject(AuxiliaryApiService);

  @Input() acnTitle = 'ACN';
  @Input() nameTitle = 'Company Name';
  @Input() hideAcn = false;
  @Input() shouldLookForCompanyName = true;
  @Input() allowedSources: ExternalSourceType[] = [ExternalSourceType.Bolt, ExternalSourceType.Xpm];

  @Output() formSubmit = new EventEmitter<{ name: string, acn: string }>();
  @Output() foundEntityId = new EventEmitter<string>();
  @Output() companySelected = new EventEmitter<Company>();

  readonly entityType = EntityType.Company;

  cantFindCompanyName = false;

  onAcnInputBlur() {
    if (this.shouldLookForCompanyName && this.form.controls.acn && this.form.controls.acn.valid) {
      this.abnLookupService.getCompanyNameByACn(String(this.form.controls.acn?.value))
        .subscribe({
          next: (result) => {
            if (!this.shouldLookForCompanyName)
              return;

            this.form.controls.name?.setValue(result.entityName);
            this.cantFindCompanyName = false;
            this.foundEntityId.emit(result.entityId);
          },
          error: () => this.cantFindCompanyName = true
        });
    } else if (this.shouldLookForCompanyName)
      this.form.controls.name?.setValue('');
  }

  onCompanySelected(company: Company): void {
    this.companySelected.emit(company);
  }

  static override defineForm(): CompanyNameAcnFormGroup {
    return new FormGroup<CompanyNameAcnFormGroupControls>(CompanyNameAcnFormGroupComponent.defineFormControls());
  }

  static override defineFormControls(): CompanyNameAcnFormGroupControls {
    return {
      acn: new FormControl<string | null>(null, {
        updateOn: 'blur',
        validators: [Validators.required, acnValidator()]
      }),
      name: new FormControl<string | null>(null, [Validators.required, Validators.minLength(7), companyNameValidator()]),
    };
  }
}
